import { FILE_VALIDATION, ERROR_MESSAGES } from '../lib/constants';

export const validateFileSize = (file) => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // 1. Minimum check (1KB = 1024 bytes)
  if (file.size < 1024) {
    return { valid: false, error: 'File is too small (minimum 1KB)' };
  }

  // 2. Maximum check (Using your constant)
  if (file.size > FILE_VALIDATION.MAX_FILE_SIZE) {
    return { 
      valid: false, 
      error: ERROR_MESSAGES.FILE_TOO_LARGE 
    };
  }

  return { valid: true };
};

/**
 * Validate file type
 */
export const validateFileType = (file) => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

const allowedTypes = FILE_VALIDATION?.SUPPORTED_TYPES || [];
const isValidType = allowedTypes.includes(file?.type);

  if (!isValidType) {
    return { 
      valid: false, 
      error: ERROR_MESSAGES.INVALID_FILE_TYPE 
    };
  }

  return { valid: true };
};

/**
 * Validate file extension
 */
export const validateFileExtension = (fileName) => {
  if (!fileName) {
    return { valid: false, error: 'No file name provided' };
  }

  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
const isValidExtension = ['.jpg', '.jpeg', '.png', '.webp'].includes(extension.toLowerCase());

  if (!isValidExtension) {
    return { 
      valid: false, 
      error: 'Invalid file extension. Use JPG, PNG, or WEBP.' 
    };
  }

  return { valid: true };
};

/**
 * Complete file validation
 */
export const validateFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file type
  const typeValidation = validateFileType(file);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  // Check file extension
  const extensionValidation = validateFileExtension(file.name);
  if (!extensionValidation.valid) {
    return extensionValidation;
  }

  // Check file size
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  return { valid: true };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Check if file is an image
 */
export const isImageFile = (file) => {
  return file?.type?.startsWith('image/') ?? false;
};

/**
 * Create image preview URL
 */
export const createImagePreview = (file) => {
  if (!isImageFile(file)) {
    return null;
  }

  return URL.createObjectURL(file);
};

/**
 * Revoke image preview URL (cleanup)
 */
export const revokeImagePreview = (url) => {
  if (url) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Convert file to base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const fileValidation = {
  validateFile,
  validateFileSize,
  validateFileType,
  validateFileExtension,
  formatFileSize,
  isImageFile,
  createImagePreview,
  revokeImagePreview,
  fileToBase64,
};

export default fileValidation;